import {
	CheckMatchPredictionsDto,
	GetProbabilityLeaderboardDto,
	HasPredictionsDto,
	LatestProbabilityEventDto,
	PaginatedLeaderboardResponseDto,
	GetUserRankDto,
	UserRankDto,
	GetUserLobbyActivityDto,
	GetUserLobbyActivityMatchDto,
	UserLobbyActivityMatchDto,
	GetMatchCrowdStatsDto,
	GetMatchPredictionStatsDto,
	MatchCrowdStatsDto,
	MatchPredictionStatsDto,
	GetLobbyMatchesDto,
	GetMyBotsDto,
	MatchWinnerItemDto,
	MyBotDto,
	BotWithPredictionsDto,
	UpdateMarketCurrentValueDto,
	LobbyMatchItemDto,
	UserLobbyActivityMatchesGroupedDto,
	UserAchievementsType,
	GetUserPredictedMatchesDto,
	UserPredictedMatchesGroupedDto,
	GetUserPerformanceDto,
	UserPerformanceResponseDto,
	GetEventFinalDataDto,
	EventSummaryStatsDto,
	FinalStandingsGroupsDto,
	GetUserParticipatedMatchesDto,
	UserParticipatedMatchItemDto,
	ProbabilityEventDetailsDto,
	GetProbabilityEventDetailsDto,
	GetShareOgPreviewDto,
	UpsertProbabilityEventDetailsDto,
	GetUserPredictedMarketsDto,
	UserPredictedMarketsBySubCategoryDto,
	OverallStatsDto,
	UpsertProbabilityScoreBoostDto,
} from '@sportspredict/shared';

export type ProbabilityAchievementItem = {
	id: string;
	type: UserAchievementsType;
	deleted_date?: string | null;
	created_date?: string | null;
	is_viewed?: boolean;
};

export type ProbabilityAchievementBacklogItem = {
	id: string;
	type: UserAchievementsType;
	is_delivered: boolean;
	created_date: string;
	matchName?: string;
	botName?: string;
};

export type UserRankWithFriendStatusDto = UserRankDto & {
	fullName?: string;
	imgVariants?: { s?: string; xs?: string } | null;
	label?: string | null;
	canBeFollowed?: boolean;
	isFollowing: boolean;
	followingId: string | null;
};

import api from '@/store/api';

export const probabilityEventApi = api.injectEndpoints({
	endpoints: (builder) => ({
		getProbabilityUserRank: builder.query<UserRankDto, GetUserRankDto>({
			query: (body) => ({
				url: '/probability/rank',
				method: 'POST',
				body,
			}),
			extraOptions: { skipAuth: true },
		}),
		getProbabilityUserRankContext: builder.query<UserRankWithFriendStatusDto, GetUserRankDto>({
			query: (body) => ({
				url: '/probability/rank/context',
				method: 'POST',
				body,
			}),
			providesTags: (
				_result,
				_error,
				{ userId, eventId, lobbyId, leaderboardType, matchId, startDate, endDate }
			) => {
				const rangeKey = startDate && endDate ? `${startDate}:${endDate}` : 'live';
				return [
					{
						type: 'Friends' as const,
						id: `${eventId}-${lobbyId}-${userId}-${leaderboardType ?? 'global'}-${matchId ?? 'nomatch'}-${rangeKey}-rank-context`,
					},
				];
			},
		}),
		getProbabilityLeaderboard: builder.query<
			PaginatedLeaderboardResponseDto,
			GetProbabilityLeaderboardDto
		>({
			query: (dto) => ({
				url: 'probability/leaderboard',
				method: 'POST',
				body: dto,
			}),
			providesTags: (
				_result,
				_error,
				{
					eventId,
					sortDirection,
					type,
					college,
					country,
					group,
					friendUserIds,
					leaderboardType,
					matchIds,
					startDate,
					endDate,
				}
			) => {
				const friendsKey = friendUserIds
					? friendUserIds.length
						? friendUserIds.join(',')
						: 'friends-none'
					: 'all-users';
				const rangeKey = startDate && endDate ? `${startDate}:${endDate}` : 'live';
				const matchKey = matchIds?.join(',') ?? 'all';

				return [
					{
						type: 'Event',
						id: `${eventId}-${sortDirection ?? 'desc'}-${type}-${college ?? 'all'}-${country ?? 'all'}-${group ?? 'all'}-${friendsKey}-${leaderboardType ?? 'global'}-${matchKey}-${rangeKey}`,
					},
				];
			},
			extraOptions: { skipAuth: true },
		}),
		getUserActivity: builder.query<UserLobbyActivityMatchesGroupedDto, GetUserLobbyActivityDto>({
			query: (data) => ({
				url: `probability/activity`,
				method: 'POST',
				body: data,
			}),
		}),
		getUserActivityMatch: builder.query<UserLobbyActivityMatchDto, GetUserLobbyActivityMatchDto>({
			query: (data) => ({
				url: 'probability/activity/match',
				method: 'POST',
				body: data,
			}),
		}),
		getUserMatchStats: builder.query<MatchPredictionStatsDto, GetMatchPredictionStatsDto>({
			query: (data) => ({
				url: `probability/match-stats`,
				method: 'POST',
				body: data,
			}),
			providesTags: (_result, _error, { matchId, lobbyId, targetUserId }) => [
				{ type: 'ProbabilityPrediction', id: `${matchId}-${lobbyId}-${targetUserId}` },
			],
		}),
		getMatchCrowdStats: builder.query<MatchCrowdStatsDto, GetMatchCrowdStatsDto>({
			query: (data) => ({
				url: 'probability/match-crowd-stats',
				method: 'POST',
				body: data,
			}),
			// Tag without search/category so a socket invalidate refreshes every cached filter variant.
			providesTags: (_result, _error, { matchId, lobbyId }) => [
				{ type: 'ProbabilityPrediction', id: `crowd-${matchId}-${lobbyId}` },
			],
			extraOptions: { skipAuth: true },
		}),
		getProbabilityLobbyMatches: builder.query<LobbyMatchItemDto[], GetLobbyMatchesDto>({
			query: (dto) => ({
				url: 'probability/lobby-matches',
				method: 'POST',
				body: dto,
			}),
			providesTags: (_result, _error, { lobbyId }) => [
				{ type: 'Game' as const, id: lobbyId },
				{ type: 'ProbabilityPrediction' as const },
			],
			extraOptions: { skipAuth: true },
		}),
		checkMatchPredictions: builder.mutation<HasPredictionsDto, CheckMatchPredictionsDto>({
			query: (data) => ({
				url: 'probability/check-predictions',
				method: 'POST',
				body: data,
			}),
		}),
		updateMarketCurrentValue: builder.mutation<void, UpdateMarketCurrentValueDto>({
			query: (data) => ({
				url: 'probability/update-odds',
				method: 'POST',
				body: data,
			}),
		}),
		getProbabilityAchievements: builder.query<
			ProbabilityAchievementItem[],
			{ userId: string; eventId?: string }
		>({
			query: (data) => ({
				url: 'probability/achievements',
				method: 'POST',
				body: data,
			}),
			providesTags: (_result, _error, { userId, eventId }) => [
				{ type: 'ProbabilityAchievements', id: eventId ? `${userId}-${eventId}` : userId },
			],
			extraOptions: { skipAuth: true },
		}),
		getProbabilityAchievementBacklog: builder.query<
			ProbabilityAchievementBacklogItem[],
			{ eventId?: string } | void
		>({
			query: (body) => ({
				url: 'probability/achievements/backlog',
				method: 'POST',
				body: body ?? {},
			}),
			providesTags: ['ProbabilityAchievements'],
		}),
		getLatestProbabilityEvent: builder.query<LatestProbabilityEventDto | null, void>({
			query: () => ({
				url: 'probability/latest-event',
				method: 'POST',
			}),
			providesTags: ['Event', 'ProbabilityPrediction', 'LobbyUser'],
		}),
		getUserPredictedMatches: builder.query<
			UserPredictedMatchesGroupedDto,
			GetUserPredictedMatchesDto
		>({
			query: (dto) => ({
				url: 'probability/user-predicted-matches',
				method: 'POST',
				body: dto,
			}),
			providesTags: (result, error, { lobbyId }) => [
				{ type: 'Game' as const, id: lobbyId },
				{ type: 'ProbabilityPrediction' as const },
			],
		}),
		getUserParticipatedMatches: builder.query<
			UserParticipatedMatchItemDto[],
			GetUserParticipatedMatchesDto
		>({
			query: (dto) => ({
				url: 'probability/user-participated-matches',
				method: 'POST',
				body: dto,
			}),
			providesTags: (_result, _error, { lobbyId }) => [
				{ type: 'Game' as const, id: lobbyId },
				{ type: 'ProbabilityPrediction' as const },
			],
		}),
		getMyBots: builder.query<MyBotDto[], GetMyBotsDto>({
			query: (body) => ({
				url: 'probability/my-bots',
				method: 'POST',
				body,
			}),
			providesTags: ['ModelApiKey'],
		}),
		getMyBotById: builder.query<MyBotDto, { botId: string } & GetMyBotsDto>({
			query: ({ botId, ...body }) => ({
				url: `probability/my-bot/${botId}`,
				method: 'POST',
				body,
			}),
			providesTags: (_result, _error, { botId }) => [{ type: 'ModelApiKey', id: botId }],
		}),
		getUserBotsWithPredictions: builder.query<BotWithPredictionsDto[], void>({
			query: () => ({
				url: 'probability/user-bots-with-predictions',
				method: 'POST',
			}),
			providesTags: ['ModelApiKey'],
		}),
		getMatchWinners: builder.query<MatchWinnerItemDto[], { eventId: string; limit?: number }>({
			query: ({ eventId, limit }) => ({
				url: `probability/match-winners/${eventId}`,
				method: 'GET',
				params: { limit },
			}),
			extraOptions: { skipAuth: true },
		}),
		createProbabilityShareLink: builder.mutation<
			{ shortCode: string },
			{ type: string; data: Record<string, unknown> }
		>({
			query: (body) => ({
				url: '/p',
				method: 'POST',
				body,
			}),
		}),
		getUserPerformance: builder.query<UserPerformanceResponseDto | null, GetUserPerformanceDto>({
			query: (body) => ({
				url: 'probability/performance',
				method: 'POST',
				body,
			}),
		}),
		getEventSummaryStats: builder.query<EventSummaryStatsDto, GetEventFinalDataDto>({
			query: (body) => ({
				url: 'probability/event-summary-stats',
				method: 'POST',
				body,
			}),
			extraOptions: { skipAuth: true },
		}),
		getFinalStandingsGroups: builder.query<FinalStandingsGroupsDto, GetEventFinalDataDto>({
			query: (body) => ({
				url: 'probability/final-standings-groups',
				method: 'POST',
				body,
			}),
			extraOptions: { skipAuth: true },
		}),
		getProbabilityEventDetails: builder.query<
			ProbabilityEventDetailsDto | null,
			GetProbabilityEventDetailsDto
		>({
			query: (body) => ({
				url: 'probability/details',
				method: 'POST',
				body,
			}),
			extraOptions: { skipAuth: true },
			providesTags: (_result, _error, { eventId }) => [
				{ type: 'ProbabilityEventDetails' as const, id: eventId },
			],
		}),
		upsertProbabilityEventDetails: builder.mutation<
			ProbabilityEventDetailsDto,
			UpsertProbabilityEventDetailsDto
		>({
			query: (body) => ({
				url: 'probability/details/upsert',
				method: 'POST',
				body,
			}),
			invalidatesTags: (_result, _error, { eventId }) => [
				{ type: 'ProbabilityEventDetails' as const, id: eventId },
			],
		}),
		upsertProbabilityEventScoreBoost: builder.mutation<
			ProbabilityEventDetailsDto,
			UpsertProbabilityScoreBoostDto
		>({
			query: (body) => ({
				url: 'probability/details/score-boost/upsert',
				method: 'POST',
				body,
			}),
			invalidatesTags: (_result, _error, { eventId }) => [
				{ type: 'ProbabilityEventDetails' as const, id: eventId },
			],
		}),
		getShareOgPreview: builder.query<string, GetShareOgPreviewDto>({
			async queryFn(body, _api, _extraOptions, baseQuery) {
				const result = await baseQuery({
					url: 'probability/details/share-og-preview',
					method: 'POST',
					body: {
						eventId: body.eventId,
						shareType: body.shareType,
						showPartnerLogo: Boolean(body.showPartnerLogo),
						...(body.partnerLogoUrl ? { partnerLogoUrl: body.partnerLogoUrl } : {}),
					},
					responseHandler: async (response: Response) => {
						const buffer = await response.arrayBuffer();
						const bytes = new Uint8Array(buffer);
						let binary = '';
						for (let i = 0; i < bytes.length; i += 1) {
							binary += String.fromCharCode(bytes[i]);
						}
						return `data:image/jpeg;base64,${btoa(binary)}`;
					},
				});

				if (result.error) {
					return { error: result.error };
				}

				return { data: String(result.data || '') };
			},
		}),
		getUserPredictedMarkets: builder.query<
			UserPredictedMarketsBySubCategoryDto,
			GetUserPredictedMarketsDto
		>({
			query: (body) => ({
				url: 'probability/user-predicted-markets-by-sub-category',
				method: 'POST',
				body,
			}),
		}),
		getUserCategoryStats: builder.query<OverallStatsDto, GetUserPredictedMarketsDto>({
			query: (body) => ({
				url: 'probability/user-category-stats',
				method: 'POST',
				body,
			}),
		}),
	}),
	overrideExisting: false,
});

export const {
	useGetProbabilityUserRankQuery,
	useGetProbabilityUserRankContextQuery,
	useLazyGetProbabilityUserRankQuery,
	useGetProbabilityLeaderboardQuery,
	useLazyGetProbabilityLeaderboardQuery,
	useGetUserActivityQuery,
	useGetUserMatchStatsQuery,
	useGetMatchCrowdStatsQuery,
	useGetProbabilityLobbyMatchesQuery,
	useCheckMatchPredictionsMutation,
	useUpdateMarketCurrentValueMutation,
	useLazyGetUserActivityMatchQuery,
	useGetUserActivityMatchQuery,
	useGetProbabilityAchievementsQuery,
	useGetProbabilityAchievementBacklogQuery,
	useGetLatestProbabilityEventQuery,
	useGetUserPredictedMatchesQuery,
	useLazyGetUserPredictedMatchesQuery,
	useGetUserParticipatedMatchesQuery,
	useLazyGetUserParticipatedMatchesQuery,
	useGetMyBotsQuery,
	useGetMyBotByIdQuery,
	useGetUserBotsWithPredictionsQuery,
	useCreateProbabilityShareLinkMutation,
	useGetUserPerformanceQuery,
	useGetEventSummaryStatsQuery,
	useGetFinalStandingsGroupsQuery,
	useGetProbabilityEventDetailsQuery,
	useUpsertProbabilityEventDetailsMutation,
	useUpsertProbabilityEventScoreBoostMutation,
	useGetShareOgPreviewQuery,
	useGetUserPredictedMarketsQuery,
	useGetUserCategoryStatsQuery,
} = probabilityEventApi;

export default probabilityEventApi;
